import React from 'react';
import KiosqueMap from '../components/KiosqueMap';
import { Reveal } from '../components/Reveal';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { MapPin, Building2, Send } from 'lucide-react';
import { useSection } from '../contexts/ContentContext';

export default function Locations() {
  const loc = useSection('locations');
  const kiosques = loc.items || [];
  const hero = loc.hero || {};
  const block = loc.communeBlock || {};
  const active = kiosques.filter((k) => k.status === 'active').length;
  const planned = kiosques.filter((k) => k.status === 'planned').length;
  const study = kiosques.filter((k) => k.status === 'study').length;

  return (
    <>
      <section className="pt-32 pb-12 bg-gradient-to-br from-sky-50 via-white to-emerald-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            {hero.eyebrow && <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-4">{hero.eyebrow}</span>}
            <h1 className="font-display font-extrabold text-5xl lg:text-7xl text-sky-950 mb-6 leading-tight">
              {hero.title} <span className="gradient-text">{hero.titleHighlight}</span>
            </h1>
            <p className="text-lg lg:text-xl text-slate-600 leading-relaxed">{hero.description}</p>
          </Reveal>

          <Reveal delay={150}>
            <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto mt-10">
              <div className="bg-white rounded-2xl p-5 border border-emerald-100">
                <div className="font-display font-extrabold text-4xl text-emerald-600">{active}</div>
                <p className="text-xs text-slate-500 mt-1 font-semibold uppercase tracking-wider">Actif</p>
              </div>
              <div className="bg-white rounded-2xl p-5 border border-sky-100">
                <div className="font-display font-extrabold text-4xl text-sky-600">{planned}</div>
                <p className="text-xs text-slate-500 mt-1 font-semibold uppercase tracking-wider">En projet</p>
              </div>
              <div className="bg-white rounded-2xl p-5 border border-amber-100">
                <div className="font-display font-extrabold text-4xl text-amber-600">{study}</div>
                <p className="text-xs text-slate-500 mt-1 font-semibold uppercase tracking-wider">À l'étude</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <KiosqueMap />

      <section className="py-20 bg-gradient-to-br from-emerald-50 to-sky-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="bg-white rounded-3xl shadow-xl p-10 border border-sky-100">
              <div className="flex flex-col lg:flex-row items-center gap-8">
                <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-sky-100 to-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-10 h-10 text-sky-700" />
                </div>
                <div className="flex-1 text-center lg:text-left">
                  <h3 className="font-display font-extrabold text-3xl text-sky-950 mb-3">{block.title}</h3>
                  <p className="text-slate-600 leading-relaxed mb-5">{block.description}</p>
                  <Link to="/contact">
                    <Button className="bg-gradient-to-r from-sky-500 to-emerald-500 text-white rounded-xl px-6">
                      {block.ctaLabel || 'Devenir ville pilote'}<Send className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-display font-extrabold text-3xl text-sky-950 mb-8 text-center">Détails par région</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {kiosques.map((k, i) => (
              <Reveal key={i} delay={i * 50}>
                <div className="p-6 rounded-3xl border border-sky-100 bg-gradient-to-br from-white to-sky-50/40 hover:shadow-lg transition-all">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-display font-bold text-lg text-sky-950 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-sky-600" />{k.name}
                    </h3>
                    <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${
                      k.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                      k.status === 'planned' ? 'bg-sky-100 text-sky-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {k.status === 'active' ? 'ACTIF' : k.status === 'planned' ? 'EN PROJET' : 'À L\'ÉTUDE'}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 mb-3">{k.region}</p>
                  <div className="flex items-center justify-between pt-3 border-t border-sky-100 text-xs">
                    <span className="text-slate-500">Ouverture</span>
                    <span className="font-semibold text-sky-900">{k.openingDate}</span>
                  </div>
                  {k.dailyUsers > 0 && (
                    <div className="flex items-center justify-between pt-2 text-xs">
                      <span className="text-slate-500">Utilisateurs / jour</span>
                      <span className="font-semibold text-emerald-700">~{k.dailyUsers}</span>
                    </div>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
